import React, { useState, useEffect, useCallback } from 'react';
import {
  AllSearchesSmartSearchResult,
  AllSearchesSmartSearchResults,
  CreateSmartSearchV1Response,
  PersonSmartSearchResults,
  VehicleSmartSearchResults,
} from '@/api/requests/llm/smartSearchV1Interfaces';
import { useSession } from '@/auth/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  SmartSearchQueryTypesEnum,
  useLLMSmartSearchV1,
} from '@/hooks/useLLMSmartSearch';
import { RawReportType } from '@/recentReports/Interfaces';
import { reportGetters, useReportRedirect } from '@/reports/index';
import {
  EmailSearchRequestBody,
  PhoneSearchRequestBody,
  PropertySearchRequestBody,
  UsernameSearchRequestBody,
  searchReport,
} from '@/reports/searchReport';
import { Feedback } from '@/reports/shared/Feedback/Feedback';
import { RecentReportsSuggestions } from '@/reports/shared/RecentReportsSuggestions/RecentReportsSuggestions';
import { ContactSearchParams, LoadingDots } from '@/search/index';
import { ObituarySearchParams } from '@/search/useSearchObituary/useSearchObituary';
import { Box, Modal, Stack, Text } from '@/theme/components';
import { Icons, useTheme } from '@/theme/index';
import { searchFormsRecyclingValidation, unformatPhone } from '@/utils/index';
import joinElements from '@/utils/joinElementsOfArray';
import { useScreenSize } from '@/utils/useScreenSize';
import { SmartSearchV1Form } from './SmartSearchV1Form';
import { StyledFilterChips, StyledLink, StyledSuggestion } from './components';
import {
  FilterChipSearchTypeObj,
  HandleFormSubmitData,
  SmartSearchPossibleParams,
  SmartSearchV1ModalProps,
  SmartSuggestion,
} from './interfaces';

export function SmartSearchV1Modal({
  isOpen = false,
  onClose,
}: SmartSearchV1ModalProps) {
  const { session } = useSession();
  const { account } = session;
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const { trackEventGA4 } = useAppConfigContext();
  const [suggestions, setSuggestions] = useState<SmartSuggestion[]>([]);
  const [selectedFilterChip, setSelectedFilterChip] = useState('');
  const [displayNoMatchError, setDisplayNoMatchError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    redirectToSearchPropertyListingsUrl,
    redirectToReportUrl,
    redirectToSearchPersonByAddressUrl,
    redirectToSearchPersonUrl,
    redirectToSearchObituaryUrl,
    redirectToSearchContactUrl,
  } = useReportRedirect();
  const {
    links: { tosLink, fcraLink },
    data: { smartSearchV1FeedbackLink, brandName },
  } = useAppConstantsContext();

  const [currentInputValue, setCurrentInputValue] = useState('');
  const [forcedSearchType, setForcedSearchType] = useState('');
  const {
    data: llmVehicleSmartSearchResponse,
    isLoading: isLoadingVehicleSmartSearch,
    isSuccess: isSuccessVehicleSmartSearch,
  } = useLLMSmartSearchV1(SmartSearchQueryTypesEnum.vehicleSmartSearchV1, {
    enabled: Boolean(currentInputValue) && forcedSearchType === 'Vehicle',
    searchValue: currentInputValue,
    shouldSuspendExecution:
      Boolean(!currentInputValue) || forcedSearchType !== 'Vehicle',
  });

  const {
    data: llmPeopleSmartSearchResponse,
    isLoading: isLoadingPeopleSmartSearch,
    isSuccess: isSuccessPeopleSmartSearch,
  } = useLLMSmartSearchV1(SmartSearchQueryTypesEnum.personSmartSearchV1, {
    enabled: Boolean(currentInputValue) && forcedSearchType === 'People',
    searchValue: currentInputValue,
    shouldSuspendExecution:
      Boolean(!currentInputValue) || forcedSearchType !== 'People',
  });

  const {
    data: llmAllReportTypesSmartSearchResponse,
    isLoading: isLoadingAllReportTypesSmartSearch,
    isSuccess: isSuccessAllReportTypesSmartSearch,
  } = useLLMSmartSearchV1(
    SmartSearchQueryTypesEnum.allReportTypesSmartSearchV1,
    {
      enabled:
        Boolean(currentInputValue) && forcedSearchType === 'AllReportTypes',
      searchValue: currentInputValue,
      shouldSuspendExecution:
        Boolean(!currentInputValue) || forcedSearchType !== 'AllReportTypes',
    }
  );

  const reportTypeByChipSelectedMap: { [key: string]: RawReportType } = {
    People: 'detailed_person_report',
    Property: 'property_report',
    Phone: 'reverse_phone_report',
    Email: 'social_network_report',
    Username: 'username_report',
    Vehicle: 'vehicle_report',
  };

  const searchTypes = [
    {
      label: 'People',
      icon: <Icons.Person fontSize="small" color="primary" />,
    },
    {
      label: 'Phone',
      icon: <Icons.Phone fontSize="small" color="primary" />,
    },
    {
      label: 'Property',
      icon: <Icons.Home fontSize="small" color="primary" />,
    },
    {
      label: 'Email',
      icon: <Icons.Email fontSize="small" color="primary" />,
    },
    {
      label: 'Username',
      icon: <Icons.AlternateEmail fontSize="small" color="primary" />,
    },
    {
      label: 'Vehicle',
      icon: <Icons.DirectionsCar fontSize="small" color="primary" />,
    },
  ];

  useEffect(() => {
    if (displayNoMatchError) {
      setSuggestions([]);
    }
  }, [displayNoMatchError]);

  useEffect(() => {
    if (selectedFilterChip) {
      if (selectedFilterChip === 'People' || selectedFilterChip === 'Vehicle') {
        setForcedSearchType(selectedFilterChip);
      } else {
        setForcedSearchType('');
      }
    } else {
      setForcedSearchType('AllReportTypes');
    }
  }, [selectedFilterChip]);

  const displayVehicleSuggestionsOrRedirectToReport = useCallback(async () => {
    if (forcedSearchType === 'Vehicle') {
      if (llmVehicleSmartSearchResponse && !isLoadingVehicleSmartSearch) {
        const llmResponse =
          llmVehicleSmartSearchResponse as CreateSmartSearchV1Response;
        if (llmResponse.error) {
          setIsLoading(false);
          setDisplayNoMatchError(true);
          return;
        }

        const results = llmResponse.results as VehicleSmartSearchResults;
        const vehicleSearchResults: Partial<AllSearchesSmartSearchResult>[] =
          results.map((result) => ({
            vin: result.vin || '',
            year: result.year || '',
            make: result.make || '',
            model: result.model || '',
            trim: result.trim || '',
            license_plate: result.license || '',
            state: result.state || '',
          }));

        generateSuggestions(vehicleSearchResults);
      } else {
        setDisplayNoMatchError(true);
      }
    }

    setIsLoading(false);
  }, [
    forcedSearchType,
    isLoadingVehicleSmartSearch,
    llmVehicleSmartSearchResponse,
  ]);

  const redirectToPersonSearchPage = useCallback(async () => {
    if (forcedSearchType === 'People') {
      if (llmPeopleSmartSearchResponse && !isLoadingPeopleSmartSearch) {
        const llmResponse =
          llmPeopleSmartSearchResponse as CreateSmartSearchV1Response;
        if (llmResponse.error) {
          setIsLoading(false);
          setDisplayNoMatchError(true);
          return;
        }

        const results = llmResponse.results as PersonSmartSearchResults;
        const personSearchResults: Partial<AllSearchesSmartSearchResult>[] =
          results.map((result) => ({
            fn: result.fn || '',
            mn: result.mn || '',
            ln: result.ln || '',
            dod: result.dod || '',
            title: result.title || '',
            company: result.company || '',
            age: result.age || '',
            city: result.city || '',
            state: result.state || '',
            street: result.street || '',
            unit: result.unit || '',
            zipcode: result.zipcode || '',
          }));

        generateSuggestions(personSearchResults);
      } else {
        setDisplayNoMatchError(true);
      }
    }

    setIsLoading(false);
  }, [
    forcedSearchType,
    isLoadingPeopleSmartSearch,
    llmPeopleSmartSearchResponse,
  ]);

  const displayAllReportTypesSuggestionsOrRedirectToReport =
    useCallback(async () => {
      if (forcedSearchType === 'AllReportTypes') {
        if (
          llmAllReportTypesSmartSearchResponse &&
          !isLoadingAllReportTypesSmartSearch
        ) {
          const llmResponse =
            llmAllReportTypesSmartSearchResponse as CreateSmartSearchV1Response;
          if (llmResponse.error) {
            setIsLoading(false);
            setDisplayNoMatchError(true);
            return;
          }

          generateSuggestions(
            llmResponse.results as AllSearchesSmartSearchResults
          );
        } else {
          setDisplayNoMatchError(true);
        }
      }

      setIsLoading(false);
    }, [llmAllReportTypesSmartSearchResponse]);

  useEffect(() => {
    const isLlmVehicleSmartSearchRequestReady =
      !isLoadingVehicleSmartSearch &&
      isSuccessVehicleSmartSearch &&
      llmVehicleSmartSearchResponse;
    const isLlmPeopleSmartSearchRequestReady =
      !isLoadingPeopleSmartSearch &&
      isSuccessPeopleSmartSearch &&
      llmPeopleSmartSearchResponse;
    const isLlmAllReportTypesSmartSearchRequestReady =
      !isLoadingAllReportTypesSmartSearch &&
      isSuccessAllReportTypesSmartSearch &&
      llmAllReportTypesSmartSearchResponse;

    if (
      forcedSearchType !== '' &&
      (isLlmVehicleSmartSearchRequestReady ||
        isLlmPeopleSmartSearchRequestReady ||
        isLlmAllReportTypesSmartSearchRequestReady)
    ) {
      if (forcedSearchType === 'People') {
        redirectToPersonSearchPage();
      } else if (forcedSearchType === 'Vehicle') {
        displayVehicleSuggestionsOrRedirectToReport();
      } else if (forcedSearchType === 'AllReportTypes') {
        displayAllReportTypesSuggestionsOrRedirectToReport();
      }

      // This is to reset the forced search type and avoid infinite loop
      setForcedSearchType('');
    }
  }, [
    forcedSearchType,
    isSuccessVehicleSmartSearch,
    isLoadingVehicleSmartSearch,
    llmVehicleSmartSearchResponse,
    displayVehicleSuggestionsOrRedirectToReport,
    isLoadingPeopleSmartSearch,
    isSuccessPeopleSmartSearch,
    llmPeopleSmartSearchResponse,
    redirectToPersonSearchPage,
    isLoadingAllReportTypesSmartSearch,
    isSuccessAllReportTypesSmartSearch,
    llmAllReportTypesSmartSearchResponse,
    displayAllReportTypesSuggestionsOrRedirectToReport,
  ]);

  const setSmartSuggestions = (possibleParams: SmartSearchPossibleParams) => {
    const results: Partial<AllSearchesSmartSearchResult> = {
      city: possibleParams.city || '',
      state: possibleParams.state || '',
      zipcode: possibleParams.zipcode || '',
      street: possibleParams.street_line || '',
      ln: possibleParams.lastName || '',
      fn: possibleParams.firstName || '',
      mn: possibleParams.middleName || '',
      phone: possibleParams.phone || '',
      email: possibleParams.email || '',
      username: possibleParams.username || '',
    };

    generateSuggestions([results]);
    setIsLoading(false);
  };

  const removeDuplicatesFrom = (suggestionsList: SmartSuggestion[]) => {
    const seen = new Set();
    return suggestionsList.filter((suggestion) => {
      const duplicate = seen.has(suggestion.label);
      seen.add(suggestion.label);
      return !duplicate;
    });
  };

  const generateSuggestions = (
    results: Partial<AllSearchesSmartSearchResult>[]
  ) => {
    const suggestionsList: SmartSuggestion[] = [];
    setIsLoading(true);

    results.forEach((result: Partial<AllSearchesSmartSearchResult>) => {
      const {
        street,
        unit,
        city,
        state,
        zipcode,
        fn,
        mn,
        ln,
        phone,
        email,
        username,
        vin,
        license_plate: licensePlate,
        year,
        make,
        model,
        trim,
        title,
        company,
        age,
        dod,
      } = result;

      // =====================
      // Property
      // =====================
      if (street && city && state && zipcode) {
        suggestionsList.push({
          icon: <Icons.Home color="primary" />,
          label: `Property report for ${street} ${city}, ${state} ${zipcode}`,
          action: () => {
            const body = {
              report_type: 'property_report',
              meta: {
                address: street || '',
                city,
                state,
                zip_code: zipcode,
                report_flags: [],
              },
            } as PropertySearchRequestBody;

            searchReport(body).then((response) => {
              if (response) {
                redirectToReportUrl('property', response?.report?.permalink);
              }
            });
          },
          gaObj: { report_type: 'property', search_by: 'address' },
        });

        suggestionsList.push({
          icon: <Icons.AccountCircle color="primary" />,
          label: `People living in ${street} ${city}, ${state} ${zipcode}`,
          action: () => {
            redirectToSearchPersonByAddressUrl({
              address: encodeURIComponent(
                `${street} ${city}, ${state} ${zipcode}`
              ),
            });
          },
          gaObj: { report_type: 'people', search_by: 'address' },
        });
      }

      if (city && state) {
        const searchParams = {
          city,
          state,
        };

        suggestionsList.push({
          icon: <Icons.MonetizationOn color="primary" />,
          label: `Listings for Sale in ${city}, ${state}`,
          action: () => {
            redirectToSearchPropertyListingsUrl({
              ...searchParams,
              search_type: 'for_sale',
            });
          },
          gaObj: { report_type: 'property', search_by: 'for_sale' },
        });

        suggestionsList.push({
          icon: <Icons.Key color="primary" />,
          label: `Listings for Rent in ${city}, ${state}`,
          action: () => {
            redirectToSearchPropertyListingsUrl({
              ...searchParams,
              search_type: 'rentals',
            });
          },
          gaObj: { report_type: 'property', search_by: 'for_rent' },
        });

        suggestionsList.push({
          icon: <Icons.Gavel color="primary" />,
          label: `Listings for Foreclosures in ${city}, ${state}`,
          action: () => {
            redirectToSearchPropertyListingsUrl({
              ...searchParams,
              search_type: 'pre_foreclosures',
            });
          },
          gaObj: { report_type: 'property', search_by: 'pre_foreclosure' },
        });
      }

      if (zipcode) {
        const searchParams = {
          zip5: zipcode,
        };

        suggestionsList.push({
          icon: <Icons.MonetizationOn color="primary" />,
          label: `Listings for Sale in ${zipcode}`,
          action: () => {
            redirectToSearchPropertyListingsUrl({
              ...searchParams,
              search_type: 'for_sale',
            });
          },
          gaObj: { report_type: 'property', search_by: 'for_sale' },
        });

        suggestionsList.push({
          icon: <Icons.Key color="primary" />,
          label: `Listings for Rent in ${zipcode}`,
          action: () => {
            redirectToSearchPropertyListingsUrl({
              ...searchParams,
              search_type: 'rentals',
            });
          },
          gaObj: { report_type: 'property', search_by: 'for_rent' },
        });

        suggestionsList.push({
          icon: <Icons.Gavel color="primary" />,
          label: `Listings for Foreclosures in ${zipcode}`,
          action: () => {
            redirectToSearchPropertyListingsUrl({
              ...searchParams,
              search_type: 'pre_foreclosures',
            });
          },
          gaObj: { report_type: 'property', search_by: 'pre_foreclosure' },
        });
      }

      // =====================
      // Phone
      // =====================
      if (phone) {
        suggestionsList.push({
          icon: <Icons.Phone color="primary" />,
          label: `Phone report for ${phone}`,
          action: () => {
            const body = {
              report_type: 'reverse_phone_report',
              meta: {
                phone_number: unformatPhone(phone),
                report_flags: [],
              },
            } as PhoneSearchRequestBody;

            searchReport(body).then((response) => {
              if (response) {
                redirectToReportUrl('phone', response.report?.permalink);
              }
            });
          },
          gaObj: { report_type: 'phone', search_by: 'phone' },
        });
      }

      // =====================
      // Username
      // =====================
      if (username) {
        suggestionsList.push({
          icon: <Icons.Gavel color="primary" />,
          label: `Social Media report for ${username}`,
          action: () => {
            const body = {
              report_type: 'username_report',
              meta: {
                username,
                report_flags: [],
              },
            } as UsernameSearchRequestBody;

            searchReport(body).then((response) => {
              if (response) {
                redirectToReportUrl('username', response?.report?.permalink);
              }
            });
          },
          gaObj: { report_type: 'username', search_by: 'username' },
        });
      }

      // =====================
      // Email
      // =====================
      if (email) {
        suggestionsList.push({
          icon: <Icons.Email color="primary" />,
          label: `Email report for ${email}`,
          action: () => {
            const body = {
              report_type: 'social_network_report',
              meta: {
                email,
                report_flags: [],
              },
            } as EmailSearchRequestBody;

            searchReport(body).then((response) => {
              if (response) {
                redirectToReportUrl('email', response?.report?.permalink);
              }
            });
          },
          gaObj: { report_type: 'email', search_by: 'email' },
        });
      }

      // =====================
      // Person
      // =====================

      // Professional
      if (fn && ln && (title || company)) {
        const contactParams: ContactSearchParams = {
          name: `${fn} ${mn} ${ln}`.trim(),
          title,
          company,
          city,
          state,
        };

        suggestionsList.push({
          icon: <Icons.Home color="primary" />,
          label: `Professional search for ${joinElements(
            [
              fn,
              ln,
              company ? `in ${company}` : '',
              title ? `as ${title}` : '',
            ],
            ' '
          )}`,
          action: () => {
            redirectToSearchContactUrl(contactParams);
          },
          gaObj: { report_type: 'people', search_by: 'address' },
        });
      }

      // Resident
      if (street && state && city && zipcode) {
        const address = joinElements(
          [
            street,
            unit ? `${unit}` : '',
            city ? `${city},` : '',
            state,
            zipcode,
          ],
          ' '
        );

        suggestionsList.push({
          icon: <Icons.AccountCircle color="primary" />,
          label: `People living in ${address}`,
          action: () => {
            redirectToSearchPersonByAddressUrl({
              address: encodeURIComponent(address),
            });
          },
          gaObj: { report_type: 'people', search_by: 'address' },
        });
      }

      // Person and Obituary
      if (ln) {
        const personSearchParams = {
          fName: fn || '',
          lName: ln || '',
          mName: mn || '',
          state: state || 'All',
          city: '',
          age: age || '',
        };

        suggestionsList.push({
          icon: <Icons.Person color="primary" />,
          label: `Person Report for ${joinElements([fn || '', ln], ' ')}`,
          action: () => {
            redirectToSearchPersonUrl(personSearchParams);
          },
          gaObj: { report_type: 'people', search_by: 'name' },
        });

        let dodMonth = '';
        let dodYear = '';
        if (dod && dod.includes('/')) {
          // Expected format from response: yyyy/mm/dd
          [dodYear, dodMonth] = dod.split('/');
        }

        const obituaryParams: ObituarySearchParams = {
          firstName: fn || '',
          lastName: ln,
          birthMonth: '',
          birthYear: '',
          deathMonth: dodMonth || '',
          deathYear: dodYear || '',
          funeralHomeCity: city || '',
          funeralHomeState: state || '',
          dod: dod || '',
        };

        suggestionsList.push({
          icon: <Icons.Home color="primary" />,
          label: `Obituary search for ${joinElements(
            [
              fn ? `${fn}` : '',
              ln,
              city || state ? 'in' : '',
              city ? `${city}` : '',
              state ? `${state}` : '',
            ],
            ' '
          )}`,
          action: () => {
            redirectToSearchObituaryUrl(obituaryParams);
          },
          gaObj: { report_type: 'obituary', search_by: 'obituary' },
        });
      }

      // =====================
      // Vehicle
      // =====================
      if (vin) {
        suggestionsList.push({
          icon: <Icons.Home color="primary" />,
          label: `VIN search for ${vin}`,
          action: async () => {
            const response = await reportGetters.vehicle({
              queryParams: new URLSearchParams({
                vin: vin || '',
              }),
            });

            redirectToReportUrl('vehicle', response?.report?.permalink);
          },
          gaObj: { report_type: 'vehicle', search_by: 'vin' },
        });
      }

      if (year && make && model && trim) {
        suggestionsList.push({
          icon: <Icons.Home color="primary" />,
          label: `Vehicle search for ${year} ${make} ${model} ${trim}`,
          action: async () => {
            const response = await reportGetters.vehicle({
              queryParams: new URLSearchParams({
                make: make || '',
                model: model || '',
                trim: trim || '',
                year: year || '',
              }),
            });

            redirectToReportUrl('vehicle', response?.report?.permalink);
          },
          gaObj: { report_type: 'vehicle', search_by: 'year_make_model' },
        });
      }

      if (licensePlate && state) {
        suggestionsList.push({
          icon: <Icons.Home color="primary" />,
          label: `Vehicle search for ${licensePlate} in ${state}`,
          action: async () => {
            const response = await reportGetters.vehicle({
              queryParams: new URLSearchParams({
                license_plate: licensePlate || '',
                state: state || '',
              }),
            });

            redirectToReportUrl('vehicle', response?.report?.permalink);
          },
          gaObj: { report_type: 'vehicle', search_by: 'license_plate' },
        });
      }
    });

    if (suggestionsList.length === 1) {
      const suggestion = suggestionsList[0];

      trackEventGA4({
        event_name: 'report_search',
        search_from: 'header_smart_search',
        ...suggestion.gaObj,
      });

      suggestion.action();
      closeModalAndReset();
    } else if (suggestionsList.length > 1) {
      setDisplayNoMatchError(false);
      const filteredSuggestions = removeDuplicatesFrom(suggestionsList);
      setSuggestions(filteredSuggestions);
    } else {
      setDisplayNoMatchError(true);
    }
  };

  const handleReset = () => {
    setDisplayNoMatchError(false);
    setSuggestions([]);
    setSelectedFilterChip('');
    setCurrentInputValue('');
    setForcedSearchType('');
    setIsLoading(false);
  };

  const closeModalAndReset = () => {
    onClose();
    handleReset();
  };

  const renderSuggestionsSection = () => {
    if (suggestions.length > 0) {
      return (
        <Stack sx={{ width: '100%' }} marginTop={2}>
          <Text variant="body1" marginBottom={1}>
            Search Results
          </Text>
          {suggestions.map((sug: SmartSuggestion, index) => {
            return (
              <StyledSuggestion
                key={`smart-search--suggestion-${index}`}
                gap={2}
                flexDirection="row"
                alignItems="center"
                onClick={() => {
                  trackEventGA4({
                    event_name: 'report_search',
                    search_from: 'header_smart_search',
                    ...sug.gaObj,
                  });
                  sug.action();
                  closeModalAndReset();
                }}
              >
                <Text>{sug.icon}</Text>
                <Text>{sug.label}</Text>
              </StyledSuggestion>
            );
          })}
        </Stack>
      );
    }
  };

  const renderRecentReportsSection = () => {
    let suggestionsTypes = Object.values(reportTypeByChipSelectedMap);

    const selectedReportType =
      reportTypeByChipSelectedMap[selectedFilterChip] || null;
    if (selectedReportType) {
      suggestionsTypes = suggestionsTypes.filter(
        (type) => type === selectedReportType
      );
    }

    return (
      <Stack
        direction="column"
        spacing={1}
        width="100%"
        marginTop="20px !important"
        data-cy="smart_search_recent_reports"
      >
        <Text variant="body1">Recent Reports</Text>

        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          my={4}
          sx={{
            width: '100%',
            marginLeft: `-${theme.spacing(1)} !important`,
          }}
        >
          {suggestionsTypes.map((reportType) => (
            <RecentReportsSuggestions
              key={reportType}
              reportType={reportType as RawReportType}
              closeForm={closeModalAndReset}
              hideLabel
            />
          ))}
        </Stack>
      </Stack>
    );
  };

  const handleFilterChipClick =
    (searchTypeObj: FilterChipSearchTypeObj) => () => {
      setSelectedFilterChip(searchTypeObj.label);
    };

  const triggerVehicleSmartSearch = (searchString: string) => {
    setIsLoading(true);
    setForcedSearchType('Vehicle');
    setCurrentInputValue(searchString);
  };

  const triggerPeopleSmartSearch = (searchString: string) => {
    setIsLoading(true);
    setForcedSearchType('People');
    setCurrentInputValue(searchString);
  };

  const triggerAllReportTypesSmartSearch = (searchString: string) => {
    setIsLoading(true);
    setForcedSearchType('AllReportTypes');
    setCurrentInputValue(searchString);
  };

  const handleFormSubmit = (data: HandleFormSubmitData) => {
    const { action } = data;

    if (action === 'closeModal') {
      closeModalAndReset();
    }

    if (!searchFormsRecyclingValidation(account, closeModalAndReset)) return;

    if (action === 'suggestions') {
      setSmartSuggestions(data.values as SmartSearchPossibleParams);
    } else if (action === 'triggerVehicleSmartSearch') {
      triggerVehicleSmartSearch(data?.searchValue || '');
    } else if (action === 'triggerPeopleSmartSearch') {
      triggerPeopleSmartSearch(data?.searchValue || '');
    } else if (action === 'triggerAllReportTypesSmartSearch') {
      triggerAllReportTypesSmartSearch(data?.searchValue || '');
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={closeModalAndReset}
      hasCloseIcon
      maxWidth={false}
      fullScreen
      closeButtonProps={{
        sx: {
          top: 35,
          right: isMobile ? 4 : 8,
          '& > svg': { fontSize: 25, color: 'primary.main' },
        },
      }}
      sx={{
        '.MuiDialog-paper': { width: '100%', height: '100%' },
        '.MuiDialogContent-root': {
          [theme.breakpoints.down('sm')]: {
            padding: '20px 16px',
          },
        },
      }}
    >
      <Stack alignItems="center" spacing={isMobile ? 2 : 1}>
        <SmartSearchV1Form
          handleSubmit={handleFormSubmit}
          clearSelectedSearchTypeFilter={() => {
            setSelectedFilterChip('');
          }}
          selectedFilterChip={selectedFilterChip}
          displayNoMatchError={displayNoMatchError}
        />

        {isLoading ? (
          <Box marginY="14px !important">
            <LoadingDots count={3} />
          </Box>
        ) : null}

        {displayNoMatchError && (
          <Box
            alignSelf="left"
            marginRight="auto !important"
            marginTop="16px !important"
          >
            <Text variant="body1">
              We didn&apos;t find an exact match for your search. Try adding
              more details to refine your search:
              <ol>
                <li>More Details: A full name, phone number, or address.</li>
                <li>
                  Check Spelling: Ensure that everything is spelled correctly.
                </li>
                <li>
                  Simplify Search: Try searching for one thing at a time, such
                  as a single person or number.
                </li>
              </ol>
              Suggestions:
              <ul>
                <li>123 Main St, New York, NY</li>
                <li>John Doe</li>
                <li>(555) 123-4567</li>
              </ul>
            </Text>
          </Box>
        )}

        {!isLoading && suggestions && suggestions.length > 0
          ? renderSuggestionsSection()
          : null}

        <Stack
          direction="column"
          spacing={1}
          width="100%"
          display={
            selectedFilterChip || suggestions.length > 0 ? 'none' : 'flex'
          }
          marginTop="20px !important"
          data-cy="smart_search_category_filters"
        >
          <Text variant="body1">I&apos;m searching for ...</Text>
          <Stack
            direction="row"
            spacing={1}
            flexWrap={isMobile ? 'wrap' : 'nowrap'}
            gap={isMobile ? 1 : 0}
            width="100%"
          >
            {searchTypes.map((searchTypeObj: FilterChipSearchTypeObj) => {
              return (
                <StyledFilterChips
                  key={searchTypeObj.label}
                  onClick={handleFilterChipClick(searchTypeObj)}
                >
                  {searchTypeObj.icon}
                  {searchTypeObj.label}
                </StyledFilterChips>
              );
            })}
          </Stack>
        </Stack>

        {renderRecentReportsSection()}

        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          spacing={isMobile ? 2 : 0}
          width="100%"
          marginTop="20px !important"
          data-cy="smart_search_disclaimer"
        >
          <Text
            variant="caption"
            width={isMobile ? '100%' : '80%'}
            fontSize="12px"
          >
            By clicking Search, you agree NOT to use results to screen/decide
            upon employment, tenants, credit, insurance, admission or other
            purposes restricted by{' '}
            <StyledLink href={fcraLink}>FCRA 15 USC 1681 et seq.</StyledLink> ,
            and acknowledge {brandName} is not a consumer reporting agency.
            Please see{' '}
            <StyledLink href={tosLink}>Terms & Conditions.</StyledLink>
          </Text>
          <Feedback
            feedbackFormUrl={smartSearchV1FeedbackLink}
            lightTheme
            removeStars
            sx={{
              marginTop: 0,

              '& .MuiButton-root': {
                fontSize: '12px',
                borderRadius: '4px',
                padding: '4px 8px',
              },
            }}
          />
        </Stack>
      </Stack>
    </Modal>
  );
}
